import { useEffect, useMemo, useRef, useState } from "react";
import AppearanceSettings from "../components/settings/AppearanceSettings";
import BackupSettings from "../components/settings/BackupSettings";
import BudgetSettings from "../components/settings/BudgetSettings";
import CategoryBudgetSettings from "../components/settings/CategoryBudgetSettings";
import CurrencySettings from "../components/settings/CurrencySettings";
import DangerZone from "../components/settings/DangerZone";
import LocalizationSettings from "../components/settings/LocalizationSettings";
import SettingsToolbar from "../components/settings/SettingsToolbar";
import {
  DEFAULT_EXCHANGE_RATES,
  accentThemes,
  categories,
  currencies,
  defaultCategoryBudgets,
  defaultCategoryOrder,
  fontSizes,
  languages,
  themeModes
} from "../constants";
import { localizeOptions } from "../utils/i18n";

const toDraft = (user) => ({
  monthlyBudget: Number(user?.monthlyBudget || 1000),
  monthlyIncome: Number(user?.monthlyIncome || 0),
  currency: user?.currency || currencies[0],
  themeMode: user?.themeMode || (user?.darkMode ? "dark" : "auto"),
  darkMode: Boolean(user?.darkMode),
  language: user?.language || languages[0],
  fontSize: user?.fontSize || fontSizes[1],
  accentColor: user?.accentColor || accentThemes[0].value,
  exchangeRates: {
    ...DEFAULT_EXCHANGE_RATES,
    ...(user?.exchangeRates || {})
  },
  categoryBudgets: {
    ...defaultCategoryBudgets,
    ...(user?.categoryBudgets || {})
  },
  categoryOrder: [
    ...(Array.isArray(user?.categoryOrder)
      ? user.categoryOrder.filter((category) => defaultCategoryOrder.includes(category))
      : []),
    ...defaultCategoryOrder.filter((category) => !(user?.categoryOrder || []).includes(category))
  ]
});

export default function SettingsPage({
  currentUser,
  locale,
  onBackup,
  onPreviewLanguage,
  onPreviewThemeMode,
  onResetAll,
  onRestore,
  onSave,
  t = {}
}) {
  const userIdRef = useRef(currentUser?.id);
  const [draft, setDraft] = useState(toDraft(currentUser));
  const [draggingCategory, setDraggingCategory] = useState("");
  const categoryOptions = localizeOptions(categories, locale, "categories");
  const languageOptions = localizeOptions(languages, locale, "languages");
  const fontSizeOptions = localizeOptions(fontSizes, locale, "fontSizes");
  const themeModeOptions = localizeOptions(themeModes, locale, "themeModes");
  const accentThemeOptions = localizeOptions(accentThemes, locale, "accentThemes");

  useEffect(() => {
    if (userIdRef.current !== currentUser?.id) {
      userIdRef.current = currentUser?.id;
      setDraft(toDraft(currentUser));
    }
  }, [currentUser]);

  const orderedCategories = useMemo(
    () =>
      [...categoryOptions].sort(
        (left, right) =>
          draft.categoryOrder.indexOf(left.value) - draft.categoryOrder.indexOf(right.value)
      ),
    [categoryOptions, draft.categoryOrder]
  );

  const updateDraft = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const updateRate = (currencyCode, value) => {
    updateDraft({
      exchangeRates: {
        ...draft.exchangeRates,
        [currencyCode]: value || 0
      }
    });
  };

  const updateCategoryBudget = (category, value) => {
    updateDraft({
      categoryBudgets: {
        ...draft.categoryBudgets,
        [category]: value || 0
      }
    });
  };

  const selectThemeMode = (themeMode) => {
    updateDraft({ themeMode, darkMode: themeMode === "dark" });
    onPreviewThemeMode(themeMode);
  };

  const reorderCategory = (targetCategory) => {
    if (!draggingCategory || draggingCategory === targetCategory) return;

    const nextOrder = [...draft.categoryOrder];
    const fromIndex = nextOrder.indexOf(draggingCategory);
    const toIndex = nextOrder.indexOf(targetCategory);

    if (fromIndex < 0 || toIndex < 0) return;

    nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, draggingCategory);
    updateDraft({ categoryOrder: nextOrder });
    setDraggingCategory("");
  };

  return (
    <div className="page-stack">
      <SettingsToolbar draft={draft} onSave={onSave} t={t} />

      <section className="settings-grid">
        <BudgetSettings draft={draft} onChange={updateDraft} t={t} />
        <LocalizationSettings
          draft={draft}
          fontSizeOptions={fontSizeOptions}
          languageOptions={languageOptions}
          onChange={updateDraft}
          onPreviewLanguage={onPreviewLanguage}
          t={t}
        />
      </section>

      <section className="settings-grid">
        <AppearanceSettings
          accentThemeOptions={accentThemeOptions}
          draft={draft}
          onAccentColorChange={(accentColor) => updateDraft({ accentColor })}
          onThemeModeChange={selectThemeMode}
          themeModeOptions={themeModeOptions}
          t={t}
        />
        <CurrencySettings draft={draft} onRateChange={updateRate} t={t} />
      </section>

      <CategoryBudgetSettings
        draft={draft}
        onBudgetChange={updateCategoryBudget}
        onDragStart={setDraggingCategory}
        onDrop={reorderCategory}
        orderedCategories={orderedCategories}
        t={t}
      />

      <section className="settings-grid">
        <BackupSettings onBackup={onBackup} onRestore={onRestore} t={t} />
        <DangerZone onResetAll={onResetAll} t={t} />
      </section>
    </div>
  );
}
